import React, { useContext, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { Trans, useTranslation } from "react-i18next";
import { DEFAULT_PUBLIC_USER_GROUP_NAME } from "common/constants";
import {
  arrayHasAtLeastOneItem,
  arrayHasExactlyOneItem,
} from "common/utils/arrays";
import { isValidEmail } from "common/utils/strings";
import { validatePassword } from "common/utils/validate-password";
import { GroupRoleType, useSignupMutation } from "../../../generated/graphql";
import { useStatusToasts } from "../../../hooks/useStatusToasts";
import { spacing } from "../../../stitches.config";
import { track } from "../../../utils/tracking";
import { AuthContext } from "../../Authorization/AuthContext";
import {
  ContentSection,
  HeaderSection,
  UpdatedContainer as Container,
} from "../../Common/__styles__/Modal";
import { Button } from "../../Common/Button";
import { Section, Sections } from "../../Common/Layout";
import { TermsOfUseInput } from "../../Common/TermsOfUseInput";
import { Body, Title } from "../../Common/Typography";
import { Select, Text } from "../../Inputs/react-hook-form";
import { PUBLIC_USER_AUTH_MODALS } from "./PublicUserAuthModal";
import { PublicUserCreateAccountErrorMessage } from "./PublicUserCreateAccountErrorMessage";

interface FormStructure {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber: Maybe<string>;
  password: string;
  terms: boolean;
  userGroupId: Maybe<string>;
}

enum CREATE_PUBLIC_USER_ACCOUNT_FORM_STEP {
  CREATE_ACCOUNT = "create-account",
  SUCCESS = "success",
}

export type ProvideCredentialsFormStructure = {
  email?: Maybe<string>;
  password?: Maybe<string>;
};

export const CreatePublicUserAccountModal = ({
  setActiveModal,
}: {
  setActiveModal: (modal: PUBLIC_USER_AUTH_MODALS) => void;
}) => {
  const { t } = useTranslation();
  const { addErrorToast } = useStatusToasts();
  const { account } = useContext(AuthContext);

  const publicUserGroups =
    account?.userGroups.filter(
      userGroup => userGroup.memberType === GroupRoleType.PUBLIC_USER
    ) ?? [];

  const minimumPasswordLength =
    account?.securityConfiguration?.minimumPasswordLength!;

  const [display, setDisplay] = useState<CREATE_PUBLIC_USER_ACCOUNT_FORM_STEP>(
    CREATE_PUBLIC_USER_ACCOUNT_FORM_STEP.CREATE_ACCOUNT
  );

  const [signup, { data, loading }] = useSignupMutation({
    onCompleted: data => {
      if (data.signup.ok) {
        track("Public user signed up");
        setDisplay(CREATE_PUBLIC_USER_ACCOUNT_FORM_STEP.SUCCESS);
      }
    },
    onError: () => {
      addErrorToast(t("sign-in-unexpected-network-error"));
    },
  });

  const defaultPublicUserGroupId =
    publicUserGroups.find(
      group => group.name === DEFAULT_PUBLIC_USER_GROUP_NAME
    )?.id ?? publicUserGroups[0]?.id;

  const useFormProps = useForm<FormStructure>({
    defaultValues: {
      userGroupId: defaultPublicUserGroupId,
    },
  });

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = useFormProps;

  const error = data?.signup.error ?? null;

  const formOnSave = async (data: FormStructure) => {
    await signup({
      variables: {
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        password: data.password,
        userGroupId: data.userGroupId!,
      },
    });
  };

  const signInLink = (
    <Button
      onClick={() => setActiveModal(PUBLIC_USER_AUTH_MODALS.SIGN_IN)}
      styleVariant="buttonLink"
      size="small"
      style={{ display: "inline", height: "auto" }}
      data-testid="sign-in-link"
    >
      {t("sign-in")}
    </Button>
  );

  return (
    <Container size="medium" overflows>
      {display === CREATE_PUBLIC_USER_ACCOUNT_FORM_STEP.CREATE_ACCOUNT && (
        <FormProvider {...useFormProps}>
          <HeaderSection>
            <Title type="semiBold" size="large">
              {t("create-your-account")}
            </Title>
          </HeaderSection>
          <ContentSection>
            <Sections
              as="form"
              gap={spacing.m}
              onSubmit={handleSubmit(formOnSave)}
            >
              {error && <PublicUserCreateAccountErrorMessage error={error} />}
              <Section>
                <Text
                  label={t("create-your-account-first-name")}
                  error={errors.firstName?.message}
                  required
                  {...register("firstName", {
                    required: t("required-field-error"),
                  })}
                />
                <Text
                  label={t("create-your-account-last-name")}
                  error={errors.lastName?.message}
                  required
                  {...register("lastName", {
                    required: t("required-field-error"),
                  })}
                />

                <Text
                  label={t("sign-in-email")}
                  error={errors.email?.message}
                  required
                  {...register("email", {
                    validate: val => {
                      if (!isValidEmail(val)) {
                        return t("signup-email-invalid");
                      }
                      return;
                    },
                    required: t("required-field-error"),
                  })}
                />
                <Text
                  label={t("sign-in-password")}
                  type="password"
                  error={errors.password?.message}
                  placeholder="********"
                  required
                  {...register("password", {
                    validate: (val: Maybe<string>) => {
                      return validatePassword({
                        password: val ?? "",
                        minimumPasswordLength,
                        t,
                      });
                    },
                    required: t("required-field-error"),
                  })}
                />
                <Body size="small" type="regular" color="contentSecondary">
                  {t("password-requirements", {
                    minimumPasswordLength,
                  })}
                </Body>
              </Section>
              {arrayHasAtLeastOneItem(publicUserGroups) && (
                <Select
                  label={t("create-your-account-role")}
                  control={control}
                  name="userGroupId"
                  error={errors.userGroupId?.message}
                  rules={{ required: t("required-field-error") }}
                  required
                  options={publicUserGroups.map(publicUserGroup => ({
                    label: publicUserGroup.name,
                    value: publicUserGroup.id,
                  }))}
                  disabled={arrayHasExactlyOneItem(publicUserGroups)}
                />
              )}
              <TermsOfUseInput control={control} errors={errors} />
              <Section gap={spacing.m}>
                <Button
                  type="submit"
                  disabled={loading}
                  size="medium"
                  styleVariant="primary"
                  stretch
                >
                  {t("create-account")}
                </Button>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    width: "100%",
                  }}
                >
                  <Body size="default" type="regular">
                    <Trans
                      i18nKey="create-your-account-footer-text"
                      components={{
                        signInLink,
                      }}
                    />
                  </Body>
                </div>
              </Section>
            </Sections>
          </ContentSection>
        </FormProvider>
      )}
      {display === CREATE_PUBLIC_USER_ACCOUNT_FORM_STEP.SUCCESS && (
        <>
          <HeaderSection>
            <Title type="semiBold" size="large">
              {t("verify-your-account-header")}
            </Title>
          </HeaderSection>
          <ContentSection>
            <Sections gap={spacing.m}>
              <Body size="default" type="regular">
                {t("verify-your-account-text")}
              </Body>
            </Sections>
          </ContentSection>
        </>
      )}
    </Container>
  );
};
